import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { getCurrencySymbol, getSymbolByCode, getSymbol } from '../utils/currency';
import axios from 'axios';
import { HomeButton } from '../components/NavButtons';
import { usePlatformLogo } from '../hooks/usePlatformSettings';
import VoiceSearchButton from '../components/VoiceSearchButton';
import {
  Car, Bed, Barbell, FirstAidKit, House, Buildings, UsersThree, CalendarBlank,
  Camera, Package, Storefront, ShoppingBag, Bank, TreeStructure, Church, Scissors,
  Martini, ForkKnife, SoccerBall, Lightning, MagnifyingGlass, MapPin, Star,
  Heart, User, List, ChatCircle, CaretRight, CaretDown, Funnel, X, HandHeart, Bell, BellRinging, GraduationCap, MoonStars
} from '@phosphor-icons/react';
import { usePushNotifications } from '../hooks/usePushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'parking', name: 'Parking', icon: Car, color: 'bg-blue-500', image: 'https://images.unsplash.com/photo-1741955693780-24dd32619f6e?w=400&h=300&fit=crop' },
  { id: 'hotels', name: 'Hotels', icon: Bed, color: 'bg-purple-500', image: 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=400&h=300&fit=crop' },
  { id: 'gyms', name: 'Gyms', icon: Barbell, color: 'bg-orange-500', image: 'https://images.pexels.com/photos/7174396/pexels-photo-7174396.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'yoga_wellness', name: 'Yoga / Wellness', icon: HandHeart, color: 'bg-pink-500', image: 'https://images.pexels.com/photos/8436684/pexels-photo-8436684.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'astrology', name: 'Astrology', icon: MoonStars, color: 'bg-violet-700', image: 'https://images.pexels.com/photos/3088369/pexels-photo-3088369.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'hospital', name: 'Hospital', icon: FirstAidKit, color: 'bg-red-500', image: 'https://images.pexels.com/photos/11660582/pexels-photo-11660582.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'home', name: 'Home', icon: House, color: 'bg-teal-500', image: 'https://images.pexels.com/photos/31737858/pexels-photo-31737858.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'coworking', name: 'Coworking', icon: Buildings, color: 'bg-indigo-500', image: 'https://images.pexels.com/photos/7071/space-desk-office-workspace.jpg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'meeting_rooms', name: 'Meeting Rooms', icon: UsersThree, color: 'bg-cyan-500', image: 'https://images.pexels.com/photos/7648016/pexels-photo-7648016.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'event_spaces', name: 'Events', icon: CalendarBlank, color: 'bg-pink-500', image: 'https://images.pexels.com/photos/16985130/pexels-photo-16985130.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'studios', name: 'Studios', icon: Camera, color: 'bg-violet-500', image: 'https://images.unsplash.com/photo-1615458508880-ec0521ce754f?w=400&h=300&fit=crop' },
  { id: 'storage', name: 'Storage', icon: Package, color: 'bg-amber-500', image: 'https://images.unsplash.com/photo-1776718489109-7c624dcdb04d?w=400&h=300&fit=crop' },
  { id: 'shops', name: 'Shops', icon: Storefront, color: 'bg-emerald-500', image: 'https://images.pexels.com/photos/31763620/pexels-photo-31763620.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'malls', name: 'Malls', icon: ShoppingBag, color: 'bg-rose-500', image: 'https://images.pexels.com/photos/33259869/pexels-photo-33259869.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'halls', name: 'Halls', icon: Bank, color: 'bg-sky-500', image: 'https://images.pexels.com/photos/32529343/pexels-photo-32529343.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'housing_societies', name: 'Societies', icon: TreeStructure, color: 'bg-lime-500', image: 'https://images.pexels.com/photos/33414231/pexels-photo-33414231.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'religious_places', name: 'Religious', icon: Church, color: 'bg-yellow-500', image: 'https://images.pexels.com/photos/14946343/pexels-photo-14946343.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'salons', name: 'Salons', icon: Scissors, color: 'bg-fuchsia-500', image: 'https://images.pexels.com/photos/29189919/pexels-photo-29189919.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'clubs', name: 'Clubs', icon: Martini, color: 'bg-slate-700', image: 'https://images.pexels.com/photos/6032557/pexels-photo-6032557.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'restaurants', name: 'Restaurants', icon: ForkKnife, color: 'bg-red-400', image: 'https://images.pexels.com/photos/35283768/pexels-photo-35283768.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'sports', name: 'Sports', icon: SoccerBall, color: 'bg-green-500', image: 'https://images.pexels.com/photos/7513413/pexels-photo-7513413.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'ev_charging', name: 'EV Charging', icon: Lightning, color: 'bg-blue-600', image: 'https://images.pexels.com/photos/4678065/pexels-photo-4678065.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
  { id: 'education', name: 'Education', icon: GraduationCap, color: 'bg-indigo-600', image: 'https://images.pexels.com/photos/5212661/pexels-photo-5212661.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop' },
];

const BuyerHome = () => {
  const navigate = useNavigate();
  const { user, logout, isSeller } = useAuth();
  const LOGO_URL = usePlatformLogo();
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: subscribePush, unsubscribe: unsubscribePush } = usePushNotifications();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestTimer, setSuggestTimer] = useState(null);
  const [listings, setListings] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState([]);
  const [availability, setAvailability] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  const [deactivatedCats, setDeactivatedCats] = useState([]);
  const [catOrder, setCatOrder] = useState([]);
  const orderedCategories = React.useMemo(() => {
    if (!catOrder.length) return CATEGORIES;
    const pos = Object.fromEntries(catOrder.map((id, i) => [id, i]));
    return [...CATEGORIES].sort((a, b) => (pos[a.id] ?? 999) - (pos[b.id] ?? 999));
  }, [catOrder]);
  const visibleCategories = React.useMemo(
    () => orderedCategories.filter(c => !deactivatedCats.includes(c.id)),
    [orderedCategories, deactivatedCats]
  );
  const [homeAds, setHomeAds] = useState({ top: [], middle: [], bottom: [] });
  const [dismissedAds, setDismissedAds] = useState({});

  useEffect(() => {
    fetchListings();
    if (user) {
      fetchWishlist();
    }
  }, [user]);

  const fetchListings = async () => {
    try {
      // Fetch active categories to filter deactivated ones
      try {
        const catRes = await axios.get(`${API_URL}/api/active-categories`);
        setDeactivatedCats(catRes.data.deactivated || []);
        setCatOrder((catRes.data.categories || []).map(c => c.category_id));
      } catch (e) { console.warn('Active categories fetch failed:', e?.message); /* fallback: show all */ }

      // Fetch homepage ads (all 3 positions)
      try {
        const [topRes, midRes, botRes] = await Promise.all([
          axios.get(`${API_URL}/api/ads?position=homepage_top`),
          axios.get(`${API_URL}/api/ads?position=homepage_middle`),
          axios.get(`${API_URL}/api/ads?position=homepage_bottom`),
        ]);
        setHomeAds({ top: topRes.data.ads || [], middle: midRes.data.ads || [], bottom: botRes.data.ads || [] });
      } catch (e) { console.warn('Ads fetch failed:', e?.message); /* no ads */ }

      const response = await axios.get(`${API_URL}/api/listings?limit=12`);
      const allListings = response.data.listings || [];
      setListings(allListings);
      
      const featuredRes = await axios.get(`${API_URL}/api/featured`);
      setFeaturedListings(featuredRes.data.featured || []);

      // Fetch availability for all listings
      const ids = allListings.map(l => l.listing_id);
      if (ids.length > 0) {
        try {
          const availRes = await axios.post(`${API_URL}/api/spaces/batch-summary`, ids);
          setAvailability(availRes.data || {});
        } catch (e) { console.warn('Batch availability fetch failed:', e?.message); /* no space data yet */ }
      }

      // Fetch unread message count
      if (user) {
        try {
          const unreadRes = await axios.get(`${API_URL}/api/chat/unread-count`);
          setUnreadCount(unreadRes.data.unread_count || 0);
        } catch (e) { console.warn('Unread count fetch failed:', e?.message); /* not logged in */ }
      }
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wishlist`);
      setWishlist(response.data.listings?.map(l => l.listing_id) || []);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.append('q', searchQuery);
    if (searchLocation) params.append('location', searchLocation);
    if (searchCategory) params.append('category', searchCategory);
    navigate(`/search?${params.toString()}`);
  };

  const handleQuickSearch = (tag) => {
    switch (tag) {
      case 'Near Me': navigate('/search?sort=nearest'); break;
      case 'Instant Book': navigate('/search?instant_booking=true'); break;
      case 'Under 500': navigate('/search?max_price=500'); break;
      default: navigate(`/search?category=${tag.toLowerCase()}`);
    }
  };

  const fetchSuggestions = (val) => {
    setSearchLocation(val);
    if (suggestTimer) clearTimeout(suggestTimer);
    if (val.length < 1) { setSuggestions([]); setShowSuggestions(false); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/search/suggestions?q=${encodeURIComponent(val)}`);
        setSuggestions(res.data.suggestions || []);
        setShowSuggestions(true);
      } catch (e) { setSuggestions([]); }
    }, 250);
    setSuggestTimer(t);
  };

  const pickSuggestion = (s) => {
    setSearchLocation(s.text);
    setShowSuggestions(false);
  };

  const SUGGESTION_ICONS = { city: MapPin, state: MapPin, pincode: MapPin, landmark: MapPin, address: MapPin, service: MagnifyingGlass, listing: Star };
  const SUGGESTION_LABELS = { city: 'City', state: 'State', pincode: 'Pincode', landmark: 'Landmark', address: 'Address', service: 'Service', listing: 'Listing' };

  const handleCategoryClick = (categoryId) => {
    navigate(`/search?category=${categoryId}`);
  };

  const toggleWishlist = async (listingId, e) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }
    try {
      if (wishlist.includes(listingId)) {
        await axios.delete(`${API_URL}/api/wishlist/${listingId}`);
        setWishlist(prev => prev.filter(id => id !== listingId));
      } else {
        await axios.post(`${API_URL}/api/wishlist/${listingId}`, {});
        setWishlist(prev => [...prev, listingId]);
      }
    } catch (error) {
      console.error('Wishlist error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo + Home */}
            <div className="flex items-center gap-1">
              <HomeButton />
              <Link to="/buyer" className="flex items-center gap-2" data-testid="logo-link">
                <img src={LOGO_URL} alt="Look4Space" className="h-10 w-auto object-contain" />
              </Link>
            </div>

            {/* Search Bar - Desktop */}
            <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-xl mx-8">
              <div className="relative w-full flex items-center gap-2">
                <div className="relative flex-1">
                  <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search spaces, city, pincode..."
                    className="w-full pl-12 pr-4 h-11 bg-slate-50 border-slate-200 rounded-full focus:bg-white focus:border-blue-500"
                    data-testid="search-input"
                  />
                </div>
                <VoiceSearchButton onResult={(text) => { setSearchQuery(text); navigate(`/search?q=${encodeURIComponent(text)}`); }} size="sm" data-testid="voice-search-desktop" />
              </div>
            </form>

            {/* Actions */}
            <div className="flex items-center gap-3">
              {user && (user.role === 'admin' || user.email === 'admin@spacebooking.com') && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/admin')}
                  className="hidden sm:flex text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="admin-panel-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 256 256" className="mr-2"><path fill="currentColor" d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm40-68a28,28,0,0,1-28,28h-8v8a8,8,0,0,1-16,0v-8h-8a28,28,0,0,1-28-28,8,8,0,0,1,16,0,12,12,0,0,0,12,12h32a12,12,0,0,0,0-24H116a28,28,0,0,1,0-56h8V72a8,8,0,0,1,16,0v8h8a28,28,0,0,1,28,28,8,8,0,0,1-16,0,12,12,0,0,0-12-12H116a12,12,0,0,0,0,24h24A28,28,0,0,1,168,148Z"/></svg>
                  Admin
                </Button>
              )}
              {isSeller && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/seller')}
                  className="hidden sm:flex text-slate-600 hover:text-blue-600"
                  data-testid="switch-to-seller-btn"
                >
                  <Buildings size={20} className="mr-2" />
                  Seller Mode
                </Button>
              )}
              {user ? (
                <>
                  {/* Quick Access Tabs for Buyers */}
                  <div className="hidden sm:flex items-center gap-1 mr-1 bg-slate-100/80 rounded-full p-1" data-testid="buyer-quick-tabs">
                    <Link
                      to="/bookings"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-slate-700 hover:bg-white hover:text-blue-600 transition-all"
                      data-testid="quick-tab-bookings"
                      title="My Bookings"
                    >
                      <List size={16} weight="bold" />
                      <span>Bookings</span>
                    </Link>
                    <Link
                      to="/wishlist"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-slate-700 hover:bg-white hover:text-rose-500 transition-all"
                      data-testid="quick-tab-saved"
                      title="Saved spaces"
                    >
                      <Heart size={16} weight="bold" />
                      <span>Saved</span>
                    </Link>
                  </div>
                  {pushSupported && (
                    <button
                      onClick={() => pushSubscribed ? unsubscribePush() : subscribePush()}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${pushSubscribed ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400 hover:text-slate-600'}`}
                      title={pushSubscribed ? 'Notifications On' : 'Enable Notifications'}
                      data-testid="push-toggle-btn"
                    >
                      {pushSubscribed ? <BellRinging size={20} weight="fill" /> : <Bell size={20} />}
                    </button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/profile')}
                    className="w-10 h-10 p-0 rounded-full"
                    data-testid="profile-btn"
                  >
                  {user.profile_photo ? (
                    <img src={user.profile_photo?.startsWith('http') ? user.profile_photo : `${API_URL}${user.profile_photo}`} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <User size={20} className="text-blue-600" />
                    </div>
                  )}
                </Button>
                </>
              ) : (
                <div className="relative group">
                  <Button
                    className="bg-blue-500 hover:bg-blue-600 text-white rounded-full px-6"
                    data-testid="login-btn"
                  >
                    Sign In
                    <CaretDown size={14} className="ml-1.5" weight="bold" />
                  </Button>
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-xl border border-slate-200 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50" data-testid="signin-dropdown">
                    <button onClick={() => navigate('/buyer/login')}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      data-testid="signin-as-buyer">
                      <MagnifyingGlass size={18} />
                      <div className="text-left">
                        <p className="font-medium">Sign In as Buyer</p>
                        <p className="text-[11px] text-slate-400">Find & book spaces</p>
                      </div>
                    </button>
                    <div className="h-px bg-slate-100 mx-3" />
                    <button onClick={() => navigate('/seller/login')}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                      data-testid="signin-as-seller">
                      <Buildings size={18} />
                      <div className="text-left">
                        <p className="font-medium">Sign In as Host</p>
                        <p className="text-[11px] text-slate-400">List & manage spaces</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Search */}
      <div className="md:hidden px-4 py-4 bg-white border-b border-slate-100">
        <form onSubmit={handleSearch}>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search spaces, city, pincode..."
                className="w-full pl-12 pr-4 h-12 bg-slate-50 border-slate-200 rounded-xl"
                data-testid="mobile-search-input"
              />
            </div>
            <VoiceSearchButton onResult={(text) => { setSearchQuery(text); navigate(`/search?q=${encodeURIComponent(text)}`); }} size="md" />
          </div>
        </form>
      </div>

      {/* Top Ad Banner */}
      <AdBanner ads={homeAds.top} position="top" dismissedAds={dismissedAds} setDismissedAds={setDismissedAds} />

      {/* Hero Section with Search */}
      <section className="relative bg-gradient-to-br from-blue-600 to-blue-700 text-white pt-8 pb-10 sm:pt-12 sm:pb-14 px-4" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-6">
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-2 font-['Outfit']">
              Find the perfect space
            </h1>
            <p className="text-base sm:text-lg text-blue-100">
              Parking, hotels, gyms, coworking & more — search by location, category or service.
            </p>
          </div>

          {/* Search Panel */}
          <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-xl shadow-blue-900/20 max-w-4xl" data-testid="search-panel">
            <form onSubmit={handleSearch}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Location / Pincode / Area */}
                <div className="relative">
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Location</label>
                  <div className="relative">
                    <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
                    <input type="text" value={searchLocation}
                      onChange={(e) => fetchSuggestions(e.target.value)}
                      onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      placeholder="City, area or pincode"
                      autoComplete="off"
                      className="w-full h-10 pl-9 pr-3 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none"
                      data-testid="search-location-input" />
                  </div>
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-slate-200 shadow-xl shadow-slate-200/50 z-50 overflow-hidden max-h-64 overflow-y-auto" data-testid="suggestions-dropdown">
                      {suggestions.map((s, i) => {
                        const Icon = SUGGESTION_ICONS[s.type] || MapPin;
                        const suggKey = `${s.type || 'sugg'}-${s.id || s.value || s.label || i}`;
                        return (
                          <button key={suggKey} type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => pickSuggestion(s)}
                            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-blue-50 transition-colors border-b border-slate-50 last:border-0"
                            data-testid={`suggestion-${i}`}>
                            <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                              <Icon size={14} className="text-slate-500" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-slate-800 font-medium truncate">{s.text}</p>
                              <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">{SUGGESTION_LABELS[s.type] || s.type}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Category */}
                <div>
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Category</label>
                  <select value={searchCategory} onChange={(e) => setSearchCategory(e.target.value)}
                    className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm text-slate-800 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none cursor-pointer"
                    data-testid="search-category-select">
                    <option value="">All Categories</option>
                    {visibleCategories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                {/* Services / Keyword */}
                <div>
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Service / Keyword</label>
                  <div className="relative">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="e.g. EV Charging, Valet"
                      className="w-full h-10 pl-9 pr-3 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none"
                      data-testid="search-keyword-input" />
                  </div>
                </div>

                {/* Search Button */}
                <div className="flex items-end gap-2">
                  <VoiceSearchButton onResult={(text) => { setSearchQuery(text); navigate(`/search?q=${encodeURIComponent(text)}`); }} size="md" className="mb-0" />
                  <button type="submit"
                    className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors shadow-lg shadow-blue-600/30"
                    data-testid="search-submit-btn">
                    <MagnifyingGlass size={16} weight="bold" />
                    Search
                  </button>
                </div>
              </div>

              {/* Quick filters */}
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
                {['Near Me', 'Instant Book', 'Under 500', 'Parking', 'Hotels', 'Gyms', 'Coworking'].map((tag) => (
                  <button key={tag} type="button"
                    onClick={() => handleQuickSearch(tag)}
                    className="px-3 py-1 bg-slate-50 hover:bg-blue-50 text-slate-600 hover:text-blue-600 rounded-full text-xs font-medium border border-slate-200 hover:border-blue-200 transition-colors"
                    data-testid={`quick-filter-${tag.toLowerCase().replace(/\s+/g, '-')}`}>
                    {tag}
                  </button>
                ))}
              </div>
            </form>
          </div>
        </div>
        {/* Decorative shapes */}
        <div className="absolute right-0 top-0 w-1/3 h-full opacity-10 pointer-events-none">
          <div className="absolute right-10 top-10 w-32 h-32 border-4 border-white rounded-3xl rotate-12"></div>
          <div className="absolute right-32 bottom-10 w-24 h-24 border-4 border-white rounded-full"></div>
        </div>
      </section>

      {/* Compact Live Sessions strip — single-line, sits right below the search hero */}
      <section className="px-4 -mt-3 sm:-mt-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/live-classes')}
            className="group w-full flex items-center justify-between gap-3 bg-gradient-to-r from-pink-500 via-fuchsia-500 to-purple-600 rounded-xl px-3 sm:px-4 py-2.5 shadow-[0_6px_18px_-6px_rgba(192,38,211,0.55)] hover:shadow-[0_10px_24px_-6px_rgba(192,38,211,0.7)] hover:-translate-y-0.5 transition-all relative overflow-hidden"
            data-testid="live-classes-banner"
          >
            {/* glassy top sheen */}
            <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/25 to-transparent pointer-events-none" />
            <span className="relative z-10 flex items-center gap-2.5 min-w-0">
              <span className="relative inline-flex items-center justify-center w-7 h-7 rounded-lg bg-white/20 ring-1 ring-white/40 shadow-inner shrink-0">
                <HandHeart size={14} weight="fill" className="text-white" />
              </span>
              <span className="text-[9px] font-extrabold tracking-wider uppercase bg-white/25 text-white px-1.5 py-0.5 rounded-full hidden sm:inline-flex">NEW</span>
              <span className="text-white font-bold text-sm sm:text-base truncate">Live Sessions</span>
              <span className="text-white/80 text-xs hidden md:inline truncate">— join expert-led video sessions</span>
            </span>
            <span className="relative z-10 flex items-center gap-1.5 bg-white text-pink-600 rounded-full px-3 py-1 text-xs font-bold shadow group-hover:bg-pink-50 shrink-0" data-testid="browse-live-classes-btn">
              Browse <CaretRight size={12} weight="bold" />
            </span>
          </button>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 font-['Outfit']">Browse Categories</h2>
            <Button
              variant="ghost"
              onClick={() => navigate('/search')}
              className="text-blue-600 hover:text-blue-700"
              data-testid="view-all-categories-btn"
            >
              View All <CaretRight size={16} className="ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-10 gap-4">
            {visibleCategories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  className="flex flex-col items-center group"
                  data-testid={`category-${category.id}-btn`}
                >
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden mb-2 shadow-lg group-hover:scale-110 transition-transform relative">
                    <img src={category.image} alt={category.name} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Icon size={24} className="text-white drop-shadow-lg" weight="fill" />
                    </div>
                  </div>
                  <span className="text-xs sm:text-sm text-slate-600 font-medium text-center">{category.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Middle Ad Banner */}
      <AdBanner ads={homeAds.middle} position="middle" dismissedAds={dismissedAds} setDismissedAds={setDismissedAds} />

      {/* Recommended Spaces */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 font-['Outfit']">Recommended for You</h2>
            <Button
              variant="ghost"
              onClick={() => navigate('/search?sort=recommended')}
              className="text-blue-600 hover:text-blue-700"
              data-testid="view-all-recommended-btn"
            >
              View All <CaretRight size={16} className="ml-1" />
            </Button>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={`recommended-skeleton-${i}`} className="bg-white rounded-2xl overflow-hidden animate-pulse flex h-36">
                  <div className="w-36 sm:w-44 bg-slate-200 shrink-0"></div>
                  <div className="p-3 flex-1 space-y-3">
                    <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                    <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                    <div className="h-3 bg-slate-200 rounded w-1/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : listings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {listings.slice(0, 8).map((listing) => (
                <ListingCard
                  key={listing.listing_id}
                  listing={listing}
                  isWishlisted={wishlist.includes(listing.listing_id)}
                  onWishlistToggle={toggleWishlist}
                  onClick={() => navigate(`/listing/${listing.listing_id}`)}
                  availability={availability[listing.listing_id]}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MagnifyingGlass size={40} className="text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No listings yet</h3>
              <p className="text-slate-500 mb-4">Be the first to list a space!</p>
              {user && (
                <Button onClick={() => navigate('/seller')} className="bg-blue-500 hover:bg-blue-600 rounded-full">
                  List Your Space
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center mb-12 font-['Outfit']">
            How SpaceBook Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <MagnifyingGlass size={32} className="text-blue-600" weight="duotone" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Search</h3>
              <p className="text-slate-600">Find the perfect space from thousands of listings across 20+ categories</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CalendarBlank size={32} className="text-emerald-600" weight="duotone" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Book</h3>
              <p className="text-slate-600">Choose your time slot, add extras, and book instantly or request approval</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Star size={32} className="text-purple-600" weight="duotone" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Enjoy</h3>
              <p className="text-slate-600">Check in with QR or OTP, use the space, and leave a review</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Ad Banner */}
      <AdBanner ads={homeAds.bottom} position="bottom" dismissedAds={dismissedAds} setDismissedAds={setDismissedAds} />

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-10 px-4" data-testid="site-footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-white font-semibold text-sm mb-3">Look4Space</h4>
              <p className="text-xs leading-relaxed">The easiest way to find and book spaces for any need — parking, coworking, events, and 18+ more categories worldwide.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-3">For Buyers</h4>
              <ul className="space-y-1.5 text-xs">
                <li><Link to="/search" className="hover:text-white transition-colors">Search Spaces</Link></li>
                <li><Link to="/bookings" className="hover:text-white transition-colors">My Bookings</Link></li>
                <li><Link to="/faq" className="hover:text-white transition-colors">Help & FAQ</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-3">For Hosts</h4>
              <ul className="space-y-1.5 text-xs">
                <li><Link to="/seller/login" className="hover:text-white transition-colors">List Your Space</Link></li>
                <li><Link to="/seller/ads" className="hover:text-white transition-colors">Promote Listings</Link></li>
                <li><Link to="/faq" className="hover:text-white transition-colors">Seller FAQ</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-3">Contact</h4>
              <ul className="space-y-1.5 text-xs">
                <li><a href="mailto:look4myspace@gmail.com" className="hover:text-white transition-colors" data-testid="footer-email">look4myspace@gmail.com</a></li>
                <li><Link to="/faq" className="hover:text-white transition-colors" data-testid="footer-faq">Help & FAQ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-4 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-[11px] text-slate-500">&copy; {new Date().getFullYear()} Look4Space. All rights reserved.</p>
            <div className="flex items-center gap-4 text-[11px]">
              <Link to="/faq" className="hover:text-white transition-colors">FAQ</Link>
              <a href="mailto:look4myspace@gmail.com" className="hover:text-white transition-colors">Support</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Bottom Navigation - Mobile */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 md:hidden z-50">
        <div className="flex items-center justify-around h-16">
          <Link to="/buyer" className="flex flex-col items-center gap-1 text-blue-600" data-testid="nav-home">
            <House size={24} weight="fill" />
            <span className="text-xs font-medium">Home</span>
          </Link>
          <Link to="/search" className="flex flex-col items-center gap-1 text-slate-500" data-testid="nav-search">
            <MagnifyingGlass size={24} />
            <span className="text-xs">Search</span>
          </Link>
          <Link to="/bookings" className="flex flex-col items-center gap-1 text-slate-500" data-testid="nav-bookings">
            <List size={24} />
            <span className="text-xs">Bookings</span>
          </Link>
          <Link to="/messages" className="flex flex-col items-center gap-1 text-slate-500 relative" data-testid="nav-messages">
            <ChatCircle size={24} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 right-0 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="unread-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
            <span className="text-xs">Messages</span>
          </Link>
          <Link to="/profile" className="flex flex-col items-center gap-1 text-slate-500" data-testid="nav-profile">
            <User size={24} />
            <span className="text-xs">Profile</span>
          </Link>
        </div>
      </nav>

      {/* Spacer for bottom nav */}
      <div className="h-16 md:hidden"></div>
    </div>
  );
};

// Listing Card Component
const CATEGORY_LABELS = {
  parking: 'Parking', hotels: 'Hotel', gyms: 'Gym', yoga_wellness: 'Yoga / Wellness', hospital: 'Hospital',
  home: 'Home Space', coworking: 'Coworking', meeting_rooms: 'Meeting Room',
  event_spaces: 'Event Space', studios: 'Studio', storage: 'Storage',
  shops: 'Shop', malls: 'Mall Kiosk', halls: 'Hall', housing_societies: 'Society Space',
  religious_places: 'Religious Space', salons: 'Salon', clubs: 'Club',
  restaurants: 'Restaurant', sports: 'Sports Turf', ev_charging: 'EV Charging'
};

const ListingCard = ({ listing, isWishlisted, onWishlistToggle, onClick, availability }) => {
  const categoryInfo = CATEGORIES.find(c => c.id === listing.category) || CATEGORIES[0];
  const Icon = categoryInfo.icon;
  const categoryLabel = CATEGORY_LABELS[listing.category] || categoryInfo.name;

  const avail = availability;
  const isFull = avail && avail.available === 0;
  const hasSlots = avail && avail.total > 0;

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-2xl overflow-hidden border border-slate-100 card-hover cursor-pointer group flex flex-row h-36"
      data-testid={`listing-card-${listing.listing_id}`}
    >
      {/* Image - Left side */}
      <div className="relative w-36 sm:w-44 shrink-0 bg-slate-100">
        {listing.images && listing.images[0] ? (
          <img src={listing.images[0]} alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon size={36} className="text-slate-300" weight="duotone" />
          </div>
        )}
        {/* Category badge */}
        <div className={`absolute top-2 left-2 ${categoryInfo.color} text-white text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1`}>
          <Icon size={11} weight="bold" />
          <span>{categoryInfo.name}</span>
        </div>
        {/* Wishlist button */}
        <button onClick={(e) => onWishlistToggle(listing.listing_id, e)}
          className="absolute top-2 right-2 w-7 h-7 bg-white/80 backdrop-blur rounded-full flex items-center justify-center hover:bg-white transition-colors"
          data-testid={`wishlist-btn-${listing.listing_id}`}>
          <Heart size={14} weight={isWishlisted ? 'fill' : 'regular'} className={isWishlisted ? 'text-red-500' : 'text-slate-600'} />
        </button>
      </div>

      {/* Content - Right side */}
      <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
        <div>
          <h3 className="font-semibold text-slate-900 text-sm line-clamp-1">{listing.title}</h3>
          <div className="flex items-center gap-1 text-slate-500 text-xs mt-1">
            <MapPin size={12} />
            <span className="line-clamp-1">{listing.address}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap mt-1">
          {hasSlots && (
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${
              isFull ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-700'
            }`} data-testid={`availability-badge-${listing.listing_id}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isFull ? 'bg-red-400' : 'bg-emerald-500'}`}></span>
              {isFull ? 'Full' : `${avail.available}/${avail.total} Slots`}
            </span>
          )}
          <div className="flex items-center gap-1">
            <Star size={12} weight="fill" className="text-yellow-400" />
            <span className="font-medium text-slate-700 text-xs">{listing.rating?.toFixed(1) || '0.0'}</span>
          </div>
        </div>

        <div className="flex items-center justify-between mt-auto pt-1">
          <div>
            <span className="font-bold text-slate-900 text-sm">{getSymbol(listing.currency || listing.country)}{listing.base_price}</span>
            <span className="text-slate-500 text-xs">/{listing.pricing_model === 'hourly' ? 'hr' : listing.pricing_model}</span>
          </div>
          <span className="text-[11px] font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full" data-testid={`book-btn-${listing.listing_id}`}>
            Book {categoryLabel}
          </span>
        </div>
      </div>
    </div>
  );
};

export default BuyerHome;

// Ad Banner Component
const AdBanner = ({ ads, position, dismissedAds, setDismissedAds }) => {
  if (!ads || ads.length === 0 || dismissedAds[position]) return null;
  const ad = ads[0];
  return (
    <div className="relative max-w-7xl mx-auto px-4 my-3" data-testid={`ad-banner-${position}`}>
      {ad.type === 'video' ? (
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl py-3 px-4 flex items-center justify-center gap-3 relative">
          <span className="bg-white/20 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">Ad</span>
          <a href={ad.link_url || ad.media_url} target="_blank" rel="noopener noreferrer" className="text-white text-sm font-medium hover:underline">{ad.title}</a>
          <button onClick={() => setDismissedAds(p => ({ ...p, [position]: true }))} className="absolute right-3 text-white/60 hover:text-white" data-testid={`dismiss-ad-${position}`}><X size={14} /></button>
        </div>
      ) : (
        <div className="relative rounded-xl overflow-hidden">
          <a href={ad.link_url || '#'} target={ad.link_url ? '_blank' : '_self'} rel="noopener noreferrer" className="block">
            <img src={ad.media_url} alt={ad.title} className="w-full h-16 sm:h-20 object-cover rounded-xl" />
          </a>
          <span className="absolute top-1.5 left-2 bg-black/50 text-white text-[8px] font-bold px-1 py-0.5 rounded">Ad</span>
          <button onClick={() => setDismissedAds(p => ({ ...p, [position]: true }))} className="absolute top-1.5 right-2 bg-black/50 hover:bg-black/70 text-white w-5 h-5 rounded-full flex items-center justify-center" data-testid={`dismiss-ad-${position}`}><X size={10} /></button>
        </div>
      )}
    </div>
  );
};
